import React, { useEffect } from 'react';
import { colors, InputText } from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import {
  RFQ_PROPOSAL_CARD_DATA,
  getRFQProposalChecklistURL,
} from 'global/checklists-helpers/RFQProposalChecklistHelper';
import RFQChecklistOptionHeader from 'components/common/headers/RFQChecklistOptionHeader';
import { Add, Delete } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useSubmitRFQPageStore } from 'store/pages/rfq/initiate-rfq/submit-rfq/pageStore';
import useRfqQuoteUrlParams from 'global/hooks/useRfqQuoteUrlParams';
import PageFooter from 'components/common/general/layouts/PageFooter';
import ContentCard from 'components/common/general/layouts/ContentCard';
import {
  AddContactButtonStyled,
  AdditionalContactHeaderStyled,
  AdditionalContactsSectionStyled,
  AdditionalContactTitleStyled,
  ButtonStyled,
  ContactFieldsListStyled,
  ContactFieldWrapperStyled,
  DefaultContactSectionStyled,
  DefaultContactTitleStyled,
  NoItemsMessageStyled,
} from './styles';
import {
  SUBMIT_RFQ_ADD_CONTACT_BTN_TEST_ID,
  SUBMIT_RFQ_ADDITIONAL_CONTACT_BLOCK_TEST_ID,
  SUBMIT_RFQ_ADDITIONAL_CONTACTS_SECTION_TEST_ID,
  SUBMIT_RFQ_DEFAULT_CONTACT_SECTION_TEST_ID,
  SUBMIT_RFQ_NO_CONTACTS_MESSAGE_TEST_ID,
} from '../constants';

const SubmitRFQContent = observer((): JSX.Element => {
  const history = useHistory();
  const { periodId, cartId } = useRfqQuoteUrlParams();

  const {
    isLoading,
    submitRFQForm,
    additionalContactFields,
    groupId,
    getInitialData,
    submitInitiateRFQ,
    getContactField,
    addContact,
    removeContact,
  } = useSubmitRFQPageStore();

  useEffect(() => {
    void getInitialData(cartId);
  }, []);

  const returnBack = (): void => {
    history.push(getRFQProposalChecklistURL({ periodId, cartId }));
  };

  const onSubmit = async (): Promise<void> => {
    // After completing this step, a new response checklist will be displayed on the RFQ checklist page.
    // The backend replace the Initiate RFQ checklist to the Response RFQ checklist
    // After this action, the user will no longer have access to the Initiate RFQ checklist.
    await submitInitiateRFQ(periodId, cartId);
    history.push(`/group/${groupId}`);
  };

  return (
    <>
      <RFQChecklistOptionHeader
        title={RFQ_PROPOSAL_CARD_DATA.SUBMIT_INITIATE_RFQ.title}
        subtitle={RFQ_PROPOSAL_CARD_DATA.SUBMIT_INITIATE_RFQ.text}
        onRedirectBack={returnBack}
      />
      <ContentCard loading={isLoading}>
        <>
          <DefaultContactSectionStyled data-test-id={SUBMIT_RFQ_DEFAULT_CONTACT_SECTION_TEST_ID}>
            <DefaultContactTitleStyled>Default Point of Contact</DefaultContactTitleStyled>
            <InputText
              label="Default email"
              id="defaultEmail"
              name="defaultEmail"
              value={submitRFQForm.fields.defaultEmail.value}
              onChange={submitRFQForm.onFieldChange}
              validationMessage={submitRFQForm.fields.defaultEmail.unpassedValidationMsg || ''}
              validationPassed={!submitRFQForm.fields.defaultEmail.unpassedValidationMsg}
              width="35rem"
            />
          </DefaultContactSectionStyled>
          <AdditionalContactsSectionStyled data-test-id={SUBMIT_RFQ_ADDITIONAL_CONTACTS_SECTION_TEST_ID}>
            <AdditionalContactHeaderStyled>
              <AdditionalContactTitleStyled>Additional Point of Contact</AdditionalContactTitleStyled>
              <AddContactButtonStyled
                color="custom"
                customColor={colors.green[600]}
                leftIconProp={<Add />}
                size="medium"
                onClick={addContact}
                data-test-id={SUBMIT_RFQ_ADD_CONTACT_BTN_TEST_ID}
              >
                Add Contact
              </AddContactButtonStyled>
            </AdditionalContactHeaderStyled>
            <ContactFieldsListStyled>
              {additionalContactFields.length > 0 ? (
                additionalContactFields.map((fieldId) => (
                  <ContactFieldWrapperStyled key={fieldId} data-test-id={SUBMIT_RFQ_ADDITIONAL_CONTACT_BLOCK_TEST_ID}>
                    <InputText
                      value={getContactField(fieldId).value}
                      name={fieldId}
                      id={fieldId}
                      onChange={submitRFQForm.onFieldChange}
                      width="35rem"
                      validationPassed={!getContactField(fieldId).unpassedValidationMsg}
                      validationMessage={getContactField(fieldId).unpassedValidationMsg || ''}
                      data-test-id={`${fieldId}-additional-email-input`}
                    />
                    <ButtonStyled
                      type="text"
                      color="custom"
                      size="small"
                      customColor={colors.red[600]}
                      onClick={() => removeContact(fieldId)}
                      leftIconProp={<Delete />}
                      data-test-id={`${fieldId}-remove-btn`}
                    >
                      Remove
                    </ButtonStyled>
                  </ContactFieldWrapperStyled>
                ))
              ) : (
                <NoItemsMessageStyled data-test-id={SUBMIT_RFQ_NO_CONTACTS_MESSAGE_TEST_ID}>
                  No additional contacts added
                </NoItemsMessageStyled>
              )}
            </ContactFieldsListStyled>
          </AdditionalContactsSectionStyled>
        </>
      </ContentCard>
      <PageFooter
        onBack={returnBack}
        onComplete={onSubmit}
        disableCompleteButton={isLoading || !submitRFQForm.isFormValid()}
      />
    </>
  );
});

export default SubmitRFQContent;
